import { Chip, IconButton } from '@material-ui/core';
import { attributes } from '@type-r/models';
import { useIO, useLinked, useModel } from '@type-r/react';
import { asCurrency, asDate, Button, Column, Columns, CsvDownloader, Flex, FormInput, InlineTable, Loading, PopupIODialog, SearchInput, SelectSortBy, TableCell, TableHeader, TableRow } from 'lqx-react';
import * as React from 'react';
import { useMemo } from 'react';
import { matchString } from 'api/common';
import { RemittanceDocumentLink } from 'common/activities/details/remittanceDocumentLink';
import { InvoiceInfo, Payment, RemittanceAdvice } from 'src/app/pages/inmatch/api';
import { RowH2 } from '../../../../../../common/inmatch';
import GetAppIcon from '@material-ui/icons/GetApp';
import { CsvMapping } from 'lqx-react/src';
import { Can } from 'src/app/services/can';
import { UserEntitlementCatalogs } from 'src/app/models/UserEntitlements';

export const ShowRemittanceAdvice = ({ payment, disabled, showExport } : {
    payment : Payment,
    disabled : boolean,
    showExport : boolean
}) => {
    const ra = useModel( RemittanceAdvice );

    const isReady = useIO( async () => {
        ra.id = payment.remittanceAdviceId as any;
        await ra.fetch();
    });

    const $unlinkingRA = useLinked( false );

    return isReady ?
    <>
        <DisplayRA ra={ ra } onUnlink={ $unlinkingRA.true } disabled={ disabled } showExport={ showExport } />
        { $unlinkingRA.value ?
            <PopupIODialog
                title={`Unlink RA`}
                $isOpen={ $unlinkingRA }
                actionLabel="Unlink"
                success="RA is unlinked"
                doIO={() => payment.linkRa( null ) }
                hideTitleOnSuccess
            >
                Unlink Remittance Advice from the payment.
            </PopupIODialog>
        : null }
    </>:
        <Loading />;
}

const DisplayRA = ({ ra, onUnlink, disabled, showExport } : {
    ra : RemittanceAdvice,
    onUnlink? : () => void,
    disabled? : boolean,
    showExport? : boolean
}) =>
    <>
        <Columns>
            <Column>
                <FormInput date label="Created On" value={ ra.createdOn } />
                <FormInput date label="Estimated Payment Date" value={ ra.estimatedPaymentDate } />
                { !disabled && onUnlink && (Can(UserEntitlementCatalogs.BankEntryWrite) || Can(UserEntitlementCatalogs.BankEntryConfirm)) ? 
                    <Flex >
                        <Button
                            onClick={ onUnlink }
                        >
                            Unlink from Payment
                        </Button>
                    </Flex>
                : null }
                <RemittanceDocumentLink label="Source file" remittanceDocument={ra.originalFileUrl} short />
            </Column>
            <Column>
                <FormInput label="Outstanding Amount" value={ asCurrency( ra.outstandingAmountFromInvoices, ra.currency ) } />
                <FormInput label="Credited Amount" value={ asCurrency( ra.creditedAmountFromInvoices, ra.currency )} />
                <FormInput label="Payment Amount" value={ asCurrency( ra.paymentAmountFromInvoices, ra.currency ) } />
            </Column>
        </Columns>

        <InlineTable
            head={
                <TableHeader>
                    <TableCell>
                        Invoice #
                    </TableCell>
                    <TableCell>
                        Outstanding Amount
                    </TableCell>
                    <TableCell>
                        Credited Amount
                    </TableCell>
                    <TableCell>
                        Payment Amount
                    </TableCell>
                    {showExport ? 
                        <TableCell>
                            <CsvDownloader filename={`invoices_${ra.paymentId}`} mapping={ csvFields } data={ ra.matches.models }>
                                <IconButton aria-label="Download" size="small">
                                    <GetAppIcon/>
                                </IconButton>
                            </CsvDownloader> 
                        </TableCell> : undefined
                    }
                    {ra.matches.some(inv => inv.unfunded) ? <TableCell></TableCell> : undefined }
                </TableHeader>
            }
        >
            { ra.matches.models.sort((a,b) => a.unfunded == b.unfunded ? 0 : a.unfunded ? 1 : -1).map( x => (
                <TableRow key={ x.cid }>
                    <TableCell>
                        { x.invoiceNumber }
                    </TableCell>
                    <TableCell>
                        { asCurrency( x.outstandingAmount, x.currency ) }
                    </TableCell>
                    <TableCell>
                        { asCurrency( x.creditedAmount, x.currency ) }
                    </TableCell>
                    <TableCell colSpan={showExport ? 2 : 1}>
                        { asCurrency( x.paymentAmount, x.currency ) }
                    </TableCell>
                    {x.unfunded ? <TableCell> <Chip label='Unfunded' variant="outlined" /></TableCell> : undefined }
                    
                </TableRow>
            ))} 
        </InlineTable>
    </>

const sortOptions = {
    closestStatementDate: 'Closest Statement Date',
    closestValue: 'Closest Amount',
    createdOn: 'Created At',
    amount: 'Payment Amount'
};

const RemittanceState = attributes({
    filter : String,
    sortBy : {
        name : "closestValue",
        isAsc : true
    }
});

export const SelectRemittanceAdvice = ({ payment } : {
    payment : Payment
}) => {
    
    const state = useModel(RemittanceState);

    const $linkingRA = useLinked<RemittanceAdvice>( null );

    const sortedAndFiltered = useMemo(() => {
        const { name, isAsc } = state.sortBy;
        return Array.from(payment.remittanceAdvices)
            .filter(ra => !state.filter || !ra.matches || ra.matches.some(inv => matchString(inv.invoiceNumber, state.filter)))            
            .sort(RemittanceAdvice.comparator(name as any, isAsc));
    },
    [ payment['_changeToken'], state['_changeToken'], payment.remittanceAdvices['_changeToken'], state.filter ]);
    
    return (
        payment.remittanceAdvices.length ?
            <>
                <Flex row>
                    <RowH2>Pick the Remittance Advices to link to the payment:</RowH2>
                </Flex>
                <Flex row>
                    <SearchInput
                        placeholder="Filter by invoice number"
                        $value={ state.$.filter }
                    />

                    <Flex grow />
                    <SelectSortBy 
                        $attr={ state.sortBy.$.name }
                        $asc={ state.sortBy.$.isAsc }
                        options={ sortOptions }
                    />
                </Flex>
                <InlineTable
                    head={
                        <TableHeader>
                            <TableCell>
                                Created On
                            </TableCell>
                            <TableCell>
                                Estimated Payment Date
                            </TableCell>
                            <TableCell>
                                Payment Amount
                            </TableCell>
                            <TableCell>
                                # Invoices
                            </TableCell>
                            <TableCell>
                                Source File
                            </TableCell>
                        </TableHeader>
                    }
                >
                    { sortedAndFiltered.map( x => (
                        <TableRow key={ x.cid }
                            onClick={ () => $linkingRA.set( x ) }
                        >
                            <TableCell>
                                { asDate( x.createdOn ) }
                            </TableCell>
                            <TableCell>
                                { asDate( x.estimatedPaymentDate ) }
                            </TableCell>
                            <TableCell>
                                { asCurrency( x.paymentAmountFromInvoices, x.currency ) }
                            </TableCell>
                            <TableCell>
                                { x.matches.length }
                            </TableCell>
                            <TableCell>
                                <RemittanceDocumentLink remittanceDocument={x.originalFileUrl} short nolabel/>
                            </TableCell>
                        </TableRow>
                    ))}                
                </InlineTable>
            
                { $linkingRA.isTruthy ?
                    <PopupIODialog
                        title={`Link RA #${$linkingRA.value.id} to the payment`}
                        $isOpen={ $linkingRA.enabled() }
                        actionLabel="Link to Payment"
                        success="Linked"
                        doIO={() => payment.linkRa( Number( $linkingRA.value.id ) ) }
                        hideTitleOnSuccess
                        disabled={!Can(UserEntitlementCatalogs.BankEntryWrite) && !Can(UserEntitlementCatalogs.BankEntryConfirm)}
                    >
                        <DisplayRA ra={ $linkingRA.value } />
                    </PopupIODialog>
                : null }
            </>
        :
            <Flex center>
                No remittance advices
            </Flex>
    )
}

const csvFields : CsvMapping<InvoiceInfo> = {
    "Invoice Number" : x => x.invoiceNumber,
    "Outstanding Amount" : x => x.outstandingAmount,
    "Credited Amount": x => x.creditedAmount,
    "Payment Amount" : x => x.paymentAmount
};
